import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { AppShell } from "@/components/app-shell";
import { ExpiryDialog } from "@/components/expiry-dialog";
import { IdleDialog } from "@/components/idle-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { ROUTES } from "@/constants/routes";
import { useIdleTimeout } from "@/hooks/use-idle-timeout";
import { api } from "@/services/api";
import { effectivePolicyQuery, profileQuery } from "@/services/queries";
import { authSnapshot, useAuthStore } from "@/store/auth";
import { useTranslation } from "@/store/locale";
import { EXPIRY_WARNING_DAYS, daysUntilPasswordExpiry } from "@/utils/password-expiry";

/**
 * The signed-in shell.
 *
 * The file-based route loaded the profile before the shell painted, so no page
 * flashed without its permissions and then rearranged itself. A data router has
 * no loader here, so the same guarantee is kept by holding the shell skeleton
 * until the profile query settles — nothing below this renders without it.
 *
 * The caller's resolved policy — lockout, password expiry, idle timeout, already
 * walked from user through group and organization — is fetched alongside. A
 * deployment that does not serve it is not a reason to hold up the app.
 */
const AppLayout = () => {
  const t = useTranslation();
  const location = useLocation();
  const { data: profile } = useQuery(profileQuery());
  const { data: policy } = useQuery(effectivePolicyQuery());
  const navigate = useNavigate();
  const signOut = useAuthStore((state) => state.signOut);
  // "Remind me later" holds for as long as the shell stays mounted — so it
  // does not follow you from page to page — and a reload asks again. The
  // warning is the only thing standing between someone and an account they
  // cannot sign in to, so it is not something to dismiss once and forget.
  const [deferred, setDeferred] = useState(false);

  const handleSignOut = (message?: string) => {
    const { refreshToken } = authSnapshot();
    // Fire and forget: the local session ends either way, and a failed revoke
    // must not strand the person on a signed-in-looking screen.
    if (refreshToken) {
      void api.post("/auth/logout", { refresh_token: refreshToken }).catch(() => undefined);
    }
    signOut();
    if (message) toast.info(message);
    void navigate(ROUTES.LOGIN);
  };

  // The policy's own number, so a deployment, an organisation, a group or one
  // person can all set it. Unset means the session never times out.
  const idle = useIdleTimeout(policy?.idle_timeout_minutes, () =>
    handleSignOut(
      t("idle.signedOut").replace("{n}", String(policy?.idle_timeout_minutes ?? "")),
    ),
  );

  if (!profile) return <ShellSkeleton />;

  // A forced change is a wall, not a suggestion: nothing else in here is
  // reachable until it is done. It cannot go in PrivateRoute — the profile that
  // decides it is only known once this layout has it.
  if (profile.force_password_change && location.pathname !== ROUTES.CHANGE_PASSWORD) {
    return <Navigate to={ROUTES.CHANGE_PASSWORD} replace />;
  }

  // The expiry is arithmetic on the policy's interval and the profile's last
  // change, so it is known on every load rather than only at sign-in.
  const remaining = daysUntilPasswordExpiry(profile, policy);
  const warn = !deferred && remaining !== null && remaining <= EXPIRY_WARNING_DAYS;

  return (
    <AppShell profile={profile} onSignOut={() => handleSignOut()}>
      {idle.secondsLeft === null ? null : (
        <IdleDialog secondsLeft={idle.secondsLeft} onStay={idle.stay} />
      )}
      {warn ? (
        <ExpiryDialog
          remaining={remaining}
          onLater={() => setDeferred(true)}
          onChange={() => void navigate(ROUTES.CHANGE_PASSWORD)}
        />
      ) : null}
      <Outlet />
    </AppShell>
  );
};

/** The shell's own shape, held while the profile loads, so nothing jumps. */
const ShellSkeleton = () => (
  <div className="grid min-h-dvh md:grid-cols-[264px_1fr]">
    <div className="bg-sidebar border-sidebar-line hidden border-r px-3 py-4 md:block">
      <Skeleton className="mx-1 h-9 w-24" />
      <Skeleton className="mt-3 h-8 rounded-lg" />
      <div className="mt-6 grid gap-1.5">
        {Array.from({ length: 8 }, (_, index) => (
          <Skeleton key={index} className="h-9 rounded-lg" />
        ))}
      </div>
    </div>
    <div>
      <div className="border-line h-[61px] border-b" />
      <div className="px-5 pt-6 md:px-8">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="mt-3 h-4 w-80" />
        <Skeleton className="rounded-card mt-6 h-96" />
      </div>
    </div>
  </div>
);

export default AppLayout;
